import React, { useEffect, useState, useRef } from 'react';
import Select from 'react-select';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser } from '../../features/userSlice';
import styles from './UnloadingEntry.module.css';
import { FaTrash } from 'react-icons/fa';
import FloatingActions from '../../components/FloatingActions';

const AssignUnloadStations = () => {
  const user = useSelector(selectUser);
  const token = user?.token;
  const emp_id = user?.emp_id; // current admin/operator
  const navigate = useNavigate();
  const topRef = useRef(null);
  const bottomRef = useRef(null);

  const [toastMessage, setToastMessage] = useState('');
  const [popup, setPopup] = useState({ open: false, title: '', message: '' });

  const [userOptions, setUserOptions] = useState([]);
  const [stationOptions, setStationOptions] = useState([]);

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);

  const [assignments, setAssignments] = useState([]); // rows: { L_PlaceId, L_PlaceName }
  const [loadingAssign, setLoadingAssign] = useState(false);
  const [loadingGrid, setLoadingGrid] = useState(false);

  const showPopup = (message, title = 'Notice') => setPopup({ open: true, title, message });
  const closePopup = () => setPopup({ open: false, title: '', message: '' });

  // --- Load Users LoV ---
  const fetchUsers = async () => {
    try {
      // Adjust if your API expects headers instead of body token
      const res = await axios.post('/api/users/getAllUsers');
      const data = res.data || [];
      console.log("Stage--1", data);
      setUserOptions(
        data.map(u => ({
          value: u.emp_id,
          label: `${u.emp_id} - ${u.Name || u.emp_name || ''}`.trim()
        }))
      );
    } catch (err) {
      console.error('getAllUsers error:', err);
      showPopup(err?.response?.data?.message || 'Failed to load users.', 'Error');
    }
  };

  // --- Load Stations LoV ---
  const fetchStations = async () => {
    try {
      const res = await axios.post('/api/wbauto/getHodUnloadingStations', { emp_id, token });
      const data = res.data || [];
      setStationOptions(data.map(s => ({ value: s.L_PlaceId, label: s.L_PlaceName })));
    } catch (err) {
      console.error('getUnloadingStations error:', err);
      showPopup(err?.response?.data?.message || 'Failed to load unloading stations.', 'Error');
    }
  };

  // --- Load Assignments for selected user ---
  const fetchAssignmentsForUser = async (targetEmpId) => {
    if (!targetEmpId) { setAssignments([]); return; }
    try {
      setLoadingGrid(true);
      const res = await axios.post('/api/wbauto/getUsersUnloadingStations', {
        emp_id: targetEmpId,
        entity_code: user?.entity_code || '', //remove if not required by backend
        token,
      });
      setAssignments(res.data || []);
    } catch (err) {
      console.error('getUsersUnloadingStations error:', err);
      setAssignments([]);
      showPopup(err?.response?.data?.message || 'Failed to load assignments.', 'Error');
    } finally {
      setLoadingGrid(false);
    }
  };

  // --- Assign ---
  const handleAssign = async () => {
    if (!selectedUser || !selectedStation) {
      setToastMessage('Please select User and Unloading Station.');
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    try {
      setLoadingAssign(true);
      const payload = {
        locationCode: selectedStation.value,
        user_id: selectedUser.value, // user being assigned
        emp_id,                      // operator id (who performs assignment)
        token
      };
      const res = await axios.post('/api/wbauto/assignUserToUnloadingStation', payload);
      if (res.data?.success) {
        setToastMessage('✅ User assigned successfully.');
        showPopup('User assigned successfully.', 'Success');
        await fetchAssignmentsForUser(selectedUser.value); // refresh grid
        setSelectedStation(null);
      } else {
        const msg = res.data?.message || 'Failed to assign.';
        setToastMessage(`❌ ${msg}`);
        showPopup(msg, 'Error');
      }
    } catch (err) {
      console.error('assignUserToUnloadingStation error:', err);
      const msg = err?.response?.data?.message || 'Error assigning user.';
      setToastMessage(`❌ ${msg}`);
      showPopup(msg, 'Error');
    } finally {
      setLoadingAssign(false);
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // --- Unassign ---
  const handleUnassign = async (placeId) => {
    if (!selectedUser) return;
    const ok = window.confirm('Do you really want to unassign this station from the user?');
    if (!ok) return;

    try {
      setLoadingAssign(true);
      const payload = {
        locationCode: placeId,
        user_id: selectedUser.value, // here backend expects emp_id = user to unassign
        emp_id,
        token
      };
      const res = await axios.post('/api/wbauto/unassignUserFromUnloadingStation', payload);
      if (res.data?.success) {
        setToastMessage('✅ Unassigned successfully.');
        showPopup('Unassigned successfully.', 'Success');
        await fetchAssignmentsForUser(selectedUser.value); // refresh grid
      } else {
        const msg = res.data?.message || 'Failed to unassign.';
        setToastMessage(`❌ ${msg}`);
        showPopup(msg, 'Error');
      }
    } catch (err) {
      console.error('unassignUserFromUnloadingStation error:', err);
      const msg = err?.response?.data?.message || 'Error unassigning.';
      setToastMessage(`❌ ${msg}`);
      showPopup(msg, 'Error');
    } finally {
      setLoadingAssign(false);
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // --- Mount ---
  useEffect(() => {
    if (!token) return;
    fetchUsers();
    fetchStations();
  }, [token]);

  // --- When user changes, load their assignments ---
  useEffect(() => {
    fetchAssignmentsForUser(selectedUser?.value);
  }, [selectedUser]);

  // --- Auto-dismiss toast ---
  useEffect(() => {
    if (!toastMessage) return;
    const t = setTimeout(() => setToastMessage(''), 3000);
    return () => clearTimeout(t);
  }, [toastMessage]);

  if (!token) {
    return (
      <div className={styles.approveContainer}>
        <p>You must be logged in to manage assignments.</p>
        <button className={styles.cancelBtn} onClick={() => navigate('/')}>Go to Login</button>
      </div>
    );
  }

  // Hide already-assigned stations from the LoV
  const assignedIds = new Set((assignments || []).map(a => a.L_PlaceId));
  const availableStationOptions = stationOptions.filter(s => !assignedIds.has(s.value));

  return (
    <div className={styles.approveContainer}>
      <div ref={topRef} />
      <h2 className={styles.screenTitle}>Assign Users to Unloading Stations</h2>

      {toastMessage && <div className={styles.toast}>{toastMessage}</div>}

      {popup.open && (
        <div className={styles.modalBackdrop} onClick={closePopup}>
          <div className={styles.modalSheet} onClick={(e) => e.stopPropagation()}>
            <h4 className={styles.modalTitle}>{popup.title}</h4>
            <p className={styles.modalBody}>{popup.message}</p>
            <button className={styles.modalCloseBtn} onClick={closePopup}>OK</button>
          </div>
        </div>
      )}

      {/* Inputs */}
      <div className={styles.filters}>
        <label>User Name:</label>
        <Select
          options={userOptions}
          value={selectedUser}
          onChange={(val) => {
            setSelectedUser(val);
            setSelectedStation(null);
          }}
          className={styles.select}
          placeholder="Select user"
          isDisabled={loadingAssign}
        />

        <label>Unloading Station:</label>
        <Select
          options={availableStationOptions}
          value={selectedStation}
          onChange={setSelectedStation}
          className={styles.select}
          placeholder="Select unloading station"
          isDisabled={!selectedUser || loadingAssign}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && selectedUser && selectedStation && !loadingAssign) {
              handleAssign();
            }
          }}
        />

        <button
          className={styles.approveBtn}
          onClick={handleAssign}
          disabled={!selectedUser || !selectedStation || loadingAssign}
        >
          {loadingAssign ? 'Working…' : 'Add Assignment'}
        </button>
      </div>

      {/* Grid */}
      <div className={styles.attendanceList}>
        <h3 style={{ marginTop: 8, marginBottom: 8 }}>Current Assignments</h3>
        <table className={styles.summaryTable}>
          <thead>
            <tr>
              <th>Unloading Station</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loadingGrid ? (
              <tr>
                <td colSpan={2} className={styles.noRecords}>Loading…</td>
              </tr>
            ) : (assignments || []).length === 0 ? (
              <tr>
                <td colSpan={2} className={styles.noRecords}>No assignments found.</td>
              </tr>
            ) : (
              assignments.map((row, idx) => (
                <tr key={`${row.L_PlaceId}-${idx}`}>
                  <td>{row.L_PlaceName || row.l_PlaceName || row.L_PlaceId}</td>
                  <td>
                    <button
                      onClick={() => handleUnassign(row.L_PlaceId)}
                      className={styles.iconButton}
                      title={`Delete ${row.L_PlaceName || row.L_PlaceId}`}
                      disabled={loadingAssign}
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <button className={styles.cancelBtn} onClick={() => navigate('/dashboard')}>
        Close
      </button>

      <FloatingActions topRef={topRef} bottomRef={bottomRef} />
      <div ref={bottomRef} />
    </div>
  );
};

export default AssignUnloadStations;
